// Math that gives the same bits in every browser (2026-11-01, online play). Lockstep online play runs the sim on every
// player's device and only sends commands, so two devices must compute the same numbers to the last bit. IEEE 754
// fixes + - * / and sqrt exactly, but not Math.hypot, Math.atan2 or Math.sin: each engine may round them its own way
// (V8, JavaScriptCore and SpiderMonkey use different libm code). The sim and the bots use these instead; a test bans
// the engine's versions in src/sim.

/** v squared. Not v ** 2: the spec lets ** round its own way per engine (x * x is exact IEEE everywhere). */
export const sq = (v: number): number => v * v;

/** Length of (x, y). Not Math.hypot: its last bit differs between engines. No overflow guard; the sim's numbers are
 *  map-sized. */
export function hypot(x: number, y: number): number {
  return Math.sqrt(x * x + y * y);
}

/** atan2 from + - * / only (an odd minimax polynomial on [0, 1], max error about 1e-5 rad), so every engine agrees. */
export function atan2(y: number, x: number): number {
  const ax = Math.abs(x), ay = Math.abs(y);
  if (ax === 0 && ay === 0) return 0;
  const swap = ay > ax;
  const t = swap ? ax / ay : ay / ax, t2 = t * t;
  let a = t * (0.99997726 + t2 * (-0.33262347 + t2 * (0.19354346 + t2 * (-0.11643287 + t2 * (0.05265332 + t2 * -0.0117212)))));
  if (swap) a = 1.5707963267948966 - a;
  if (x < 0) a = 3.141592653589793 - a;
  return y < 0 ? -a : a;
}

/** sin from + - * / only: range-reduced to [-pi, pi], then the Taylor series to x^17 (error under 1e-6 there). */
export function sin(v: number): number {
  const TAU = 6.283185307179586;
  const x = v - TAU * Math.floor(v / TAU + 0.5); // Math.floor is exact in every engine
  const x2 = x * x;
  let s = 0;
  for (let k = 8; k >= 1; k--) s = 1 - s * x2 / ((2 * k) * (2 * k + 1)); // Horner on the series, innermost term first
  return x * s;
}
